import { useRef, useEffect, useState, useMemo } from "react";
import type { ChatMessage, ChatToolUse, ChatToolResult } from "../types/chat";

const PHASE_LABELS: Record<string, string> = {
  planning: "Planning...",
  executing: "Browsing...",
  evaluating: "Analyzing...",
  refining: "Refining...",
  // Task 175: shown while a message waits for its `off` session to resume (Off→Starting→On).
  resuming: "Reprise de la session…",
};

// Task 158: readable labels for the model INDICATOR (the picker is gone — the backend
// resolves the model, 155). Local to this component on purpose (the hook no longer exports
// a model list). An unknown id falls back to the raw id.
const MODEL_LABELS: Record<string, string> = {
  "claude-opus-5-5": "Opus 5.5",
  "claude-sonnet-5": "Sonnet 5",
  "claude-opus-4-8": "Opus 4.8",
  "claude-haiku-4-5": "Haiku 4.5",
};

interface ChatPanelProps {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  phase: string | null;
  iteration?: { n: number; max: number } | null;
  budgetExhausted?: boolean;
  onContinue?: () => void;
  onSend: (text: string) => void;
  onClear: () => void;
  onCancel: () => void;
  onClose: () => void;
  activeModel: string | null;
  agentMode?: "cdp" | "a11y" | "visual";
  // Task 172: generate the summary (169) — adds a card to the download list instead of downloading.
  onGenerateSummary?: () => void;
  // Task 172: download one file (a card's "Télécharger" and "Tout télécharger") via the 139 route.
  onDownloadFile?: (filename: string, downloadUrl: string) => void;
  // Task 172: the generated summary file, shown as the first download card once produced.
  summaryFile?: { filename: string; downloadUrl: string } | null;
  summaryLoading?: boolean;
}

export default function ChatPanel({ messages, isLoading, error, phase, iteration, budgetExhausted, onContinue, onSend, onClear, onCancel, onClose, activeModel, agentMode, onGenerateSummary, onDownloadFile, summaryFile, summaryLoading }: ChatPanelProps) {
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const stickToBottomRef = useRef(true);

  // Track if user scrolled away from bottom — only auto-scroll if they're at/near the bottom
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottomRef.current = distanceFromBottom < 40;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // Auto-scroll only if user was already near bottom
  useEffect(() => {
    if (stickToBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleSubmit = () => {
    if (!input.trim() || isLoading) return;
    onSend(input.trim());
    setInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Task 172: the download list = the generated summary (169, if any) first, then every successful
  // export_data (139) found in the conversation. Both are derived from data the front already has
  // (summaryFile state + the tool_result JSON), so no backend list endpoint is needed. Dedup by URL
  // in case the same file surfaces twice.
  const downloadFiles = useMemo(() => {
    const all = summaryFile ? [summaryFile, ...exportCardsFromMessages(messages)] : exportCardsFromMessages(messages);
    const seen = new Set<string>();
    return all.filter(f => (seen.has(f.downloadUrl) ? false : (seen.add(f.downloadUrl), true)));
  }, [messages, summaryFile]);

  return (
    <div style={{
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      background: "#111827",
      borderRight: "1px solid #1f2937",
    }}>
      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 12px",
        borderBottom: "1px solid #1f2937",
        flexShrink: 0,
      }}>
        <span style={{ color: "#e5e7eb", fontSize: 13, fontWeight: 600 }}>
          Chat with Claude
        </span>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span
            title={activeModel ?? undefined}
            style={{
              background: "#1f2937",
              color: "#9ca3af",
              border: "1px solid #374151",
              borderRadius: 4,
              fontSize: 11,
              padding: "2px 6px",
            }}
          >
            {activeModel ? (MODEL_LABELS[activeModel] ?? activeModel) : "—"}
          </span>
          {agentMode === "visual" && (
            <span style={{
              background: "#7c3aed",
              color: "#ede9fe",
              borderRadius: 4,
              fontSize: 10,
              padding: "2px 6px",
              fontWeight: 600,
            }}>
              {"\uD83D\uDC41"} Visual
            </span>
          )}
          {/* Task 173: keep the button visible during a run (discoverability) — greyed + disabled
              instead of hidden, so the download entry point never disappears while the agent works. */}
          {onGenerateSummary && messages.length > 0 && (
            <button
              onClick={onGenerateSummary}
              disabled={isLoading || summaryLoading}
              style={{ ...headerBtnStyle, opacity: (isLoading || summaryLoading) ? 0.4 : 1 }}
              title={isLoading ? "Disponible une fois la réponse terminée" : "Générer le résumé (une carte de téléchargement s'ajoutera à la liste)"}
            >
              {summaryLoading ? "…" : "📄 Résumé"}
            </button>
          )}
          <button onClick={onClear} style={headerBtnStyle} title="Clear chat">
            &#x27F3;
          </button>
          <button onClick={onClose} style={headerBtnStyle} title="Close">
            &#x2715;
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} style={{
        flex: 1,
        overflowY: "auto",
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}>
        {messages.length === 0 && (
          <div style={{ color: "#6b7280", fontSize: 12, textAlign: "center", marginTop: 40 }}>
            Ask Claude to browse the web for you.
            <br />
            Example: "Go to ovhcloud.com"
          </div>
        )}
        {messages.map((msg, i) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            /* Task 171: "Terminé" pill only once this message's run is over — i.e. it is not the
               last message, or the panel is no longer loading. */
            isDone={i < messages.length - 1 || !isLoading}
          />
        ))}
        {isLoading && (
          <div style={{ color: "#9ca3af", fontSize: 12, padding: "4px 8px" }}>
            {phase ? (PHASE_LABELS[phase] || phase) : "Claude is thinking..."}
            {phase === "executing" && iteration && (
              <span style={{ color: "#6b7280", marginLeft: 6 }}>
                ({iteration.n}/{iteration.max})
              </span>
            )}
          </div>
        )}
        {!isLoading && budgetExhausted && onContinue && (
          <div style={{
            background: "#451a03",
            border: "1px solid #92400e",
            borderRadius: 6,
            padding: "6px 10px",
            fontSize: 12,
            color: "#fcd34d",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}>
            <span>Budget d'itérations épuisé — la tâche est peut-être incomplète.</span>
            <button
              onClick={onContinue}
              style={{ ...sendBtnStyle, background: "#d97706", padding: "4px 10px", fontSize: 12 }}
            >
              Continuer
            </button>
          </div>
        )}
        {error && (
          <div style={{ color: "#f87171", fontSize: 12, padding: "4px 8px" }}>
            Error: {error}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Task 172/173: download cards below the conversation. Task 173 makes it show a persistent
          "Résumé de la conversation" action card whenever there are messages but no real file yet,
          so the download entry point is discoverable in a text-only conversation. */}
      {onDownloadFile && (
        <DownloadCards
          files={downloadFiles}
          onDownload={onDownloadFile}
          hasMessages={messages.length > 0}
          summaryFile={summaryFile}
          summaryLoading={summaryLoading}
          onGenerateSummary={onGenerateSummary}
        />
      )}

      {/* Input */}
      <div style={{
        padding: "8px 10px",
        borderTop: "1px solid #1f2937",
        display: "flex",
        gap: 6,
        flexShrink: 0,
      }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={agentMode === "visual" ? "Describe what to do on screen..." : "Message Claude..."}
          rows={1}
          style={{
            flex: 1,
            background: "#1f2937",
            color: "#e5e7eb",
            border: "1px solid #374151",
            borderRadius: 6,
            padding: "6px 10px",
            fontSize: 13,
            resize: "none",
            outline: "none",
            fontFamily: "inherit",
          }}
        />
        {isLoading ? (
          <button onClick={onCancel} style={{ ...sendBtnStyle, background: "#dc2626" }}>
            Stop
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={!input.trim()} style={sendBtnStyle}>
            Send
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────

// Render markdown links [text](url) as clickable anchors; everything else as plain text
function renderWithLinks(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const re = /\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <a
        key={m.index}
        href={m[2]}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#93c5fd", textDecoration: "underline", wordBreak: "break-all" }}
      >
        {m[1]}
      </a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function MessageBubble({ message, isDone }: { message: ChatMessage; isDone: boolean }) {
  const isUser = message.role === "user";
  // Task 171: track the <details> open state in React (not a CSS `details[open]` selector — this
  // file uses inline styles only) so the chevron can rotate on expand. `onToggle` keeps the native
  // keyboard a11y of <details>. Local per MessageBubble (keyed by msg.id) → per-message fold state.
  const [open, setOpen] = useState(false);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: isUser ? "flex-end" : "flex-start" }}>
      {/* Main content */}
      {message.content && (
        <div style={{
          background: isUser ? "#2563eb" : "#1f2937",
          color: "#e5e7eb",
          padding: "6px 10px",
          borderRadius: 8,
          fontSize: 13,
          maxWidth: "85%",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}>
          {isUser ? message.content : renderWithLinks(message.content)}
        </div>
      )}

      {/* Task 170/171: the agent's "reasoning" (tool calls + results + narration) grouped under a
          single collapsible bar, COLLAPSED by default. Data/logic are UNCHANGED from 170 (same
          display condition, same N = tool-call count); 171 only restyles this block to the mockup
          (bar + accent border + rotating chevron + left timeline + clock icon + bottom fade +
          "Terminé" pill). Native <details> keeps keyboard a11y; `open` (React) drives the chevron
          and is local per keyed MessageBubble → per-message fold state (170 non-regression). */}
      {((message.toolUses?.length ?? 0) > 0 || message.narration) && (
        <details
          onToggle={e => setOpen((e.target as HTMLDetailsElement).open)}
          style={{
            marginTop: 4,
            maxWidth: "85%",
            width: "100%",
            border: `1px solid ${open ? "#2563eb" : "#374151"}`,
            borderRadius: 8,
            background: "#1f2937",
            overflow: "hidden",
          }}
        >
          <summary style={{
            cursor: "pointer",
            fontSize: 12,
            color: "#9ca3af",
            userSelect: "none",
            padding: "6px 10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            listStyle: "none",
          }}>
            <span>Réflexions de l'agent ({message.toolUses?.length ?? 0} étapes)</span>
            <span style={{
              display: "inline-block",
              color: "#6b7280",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.15s ease",
            }}>{"›"}</span>
          </summary>
          {/* Left timeline + clock, bounded height with a bottom fade (persistent, per mockup);
              scrollable so the full reasoning stays reachable (see CHANGES.md). The mask fades to
              transparent, revealing the #1f2937 bar background — no light halo on the dark theme. */}
          <div style={{
            borderLeft: "2px solid #374151",
            marginLeft: 14,
            paddingLeft: 12,
            paddingBottom: 8,
            paddingRight: 10,
            maxHeight: 220,
            overflowY: "auto",
            WebkitMaskImage: "linear-gradient(to bottom, black 78%, transparent 100%)",
            maskImage: "linear-gradient(to bottom, black 78%, transparent 100%)",
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginTop: 6 }}>
              <span style={{ fontSize: 11 }}>{"🕐"}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {message.narration && (
                  <div style={{ fontSize: 12, color: "#9ca3af", whiteSpace: "pre-wrap", marginBottom: 4 }}>
                    {message.narration}
                  </div>
                )}
                {message.toolUses?.map(tu => (
                  <ToolUseBlock key={tu.id} toolUse={tu} />
                ))}
                {message.toolResults?.map(tr => (
                  <ToolResultBlock key={tr.toolUseId} result={tr} />
                ))}
              </div>
            </div>
          </div>
          {isDone && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              color: "#6b7280",
              padding: "4px 10px 8px 26px",
            }}>
              <span>{"✓"}</span><span>Terminé</span>
            </div>
          )}
        </details>
      )}
    </div>
  );
}

function ToolUseBlock({ toolUse }: { toolUse: ChatToolUse }) {
  const preview = toolUse.name.startsWith("mouse.") && toolUse.input.x !== undefined
    ? `(${toolUse.input.x}, ${toolUse.input.y})`
    : JSON.stringify(toolUse.input).slice(0, 100);
  return (
    <div style={{
      background: "#1e1b4b",
      border: "1px solid #312e81",
      borderRadius: 6,
      padding: "4px 8px",
      fontSize: 12,
      color: "#c7d2fe",
      marginTop: 4,
      maxWidth: "85%",
    }}>
      <span style={{ fontWeight: 600 }}>{"\uD83D\uDD27"} {toolUse.name}</span>
      {Object.keys(toolUse.input).length > 0 && (
        <span style={{ color: "#a5b4fc", marginLeft: 6 }}>
          {preview}
        </span>
      )}
    </div>
  );
}

function ToolResultBlock({ result }: { result: ChatToolResult }) {
  const [expanded, setExpanded] = useState(false);

  if (result.isScreenshot) {
    return (
      <div style={{ marginTop: 4, maxWidth: "85%" }}>
        <img
          src={`data:image/png;base64,${result.content}`}
          alt="Screenshot"
          style={{ maxWidth: "100%", borderRadius: 6, border: "1px solid #374151" }}
        />
      </div>
    );
  }

  const preview = result.content.slice(0, 80);
  const hasMore = result.content.length > 80;

  return (
    <div
      style={{
        background: "#022c22",
        border: "1px solid #064e3b",
        borderRadius: 6,
        padding: "4px 8px",
        fontSize: 12,
        color: "#a7f3d0",
        marginTop: 4,
        maxWidth: "85%",
        cursor: hasMore ? "pointer" : "default",
      }}
      onClick={() => hasMore && setExpanded(e => !e)}
    >
      <span style={{ fontWeight: 600 }}>{"\u2713"} Result: </span>
      {expanded ? result.content : preview}
      {hasMore && !expanded && "..."}
    </div>
  );
}

// ─── Task 172: download cards ────────────────────

// Extension → readable file-type label for the card subtitle ("Document · PDF", etc.).
const FILE_TYPE_LABELS: Record<string, string> = {
  pdf: "PDF", md: "MD", json: "JSON", csv: "CSV", txt: "TXT", html: "HTML",
};
function fileTypeLabel(filename: string): string {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  return `Document · ${FILE_TYPE_LABELS[ext] ?? (ext ? ext.toUpperCase() : "Fichier")}`;
}

// The mockup shows a human-readable TITLE (spaces, no extension), not the raw filename. Derive it:
// drop the extension, turn separators into spaces. The real filename (with extension) is still used
// for the actual download (a.download in downloadFile), so nothing about the download changes.
function displayTitle(filename: string): string {
  const dot = filename.lastIndexOf(".");
  const stem = dot > 0 ? filename.slice(0, dot) : filename;
  return stem.replace(/[_-]+/g, " ").trim() || filename;
}

// Extract a single top-level JSON string field ("key":"value") from a raw string WITHOUT parsing the
// whole document. Robust to two things a full JSON.parse is NOT:
//   1. Key ORDER — the backend serialises with serde_json's default Map (BTreeMap, no `preserve_order`
//      feature; see backend logs_ingest.rs:170), i.e. keys come out ALPHABETICALLY, not in insertion
//      order. We must not assume `download_url`/`filename` are "first".
//   2. TRUNCATION — StoredToolResult.content is truncated to 2000 chars, which usually leaves the tail
//      JSON unterminated → JSON.parse throws on reloaded history. Both fields we need are short and
//      well under 2000 chars, so their "key":"value" substrings are intact even when the doc is cut.
// The captured body is JSON-unescaped via a targeted JSON.parse of just that string literal.
function extractJsonStringField(raw: string, key: string): string | null {
  const m = raw.match(new RegExp(`"${key}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`));
  const body = m?.[1];
  if (body === undefined) return null;
  try { return JSON.parse(`"${body}"`) as string; } catch { return body; }
}

// Scan the conversation for successful export_data (139) files. Each such tool_result's content is
// stringified JSON carrying download_url + filename. We extract just those two fields by regex
// (see extractJsonStringField) rather than JSON.parse, so the derivation is independent of key order
// and survives the 2000-char store truncation on reloaded history. A failed export's content has no
// such fields → both null → skipped (no card). Derived purely from `messages`, which the front
// already has for every message (incl. after a reload), so no backend list endpoint is needed.
function exportCardsFromMessages(messages: ChatMessage[]): { filename: string; downloadUrl: string }[] {
  const cards: { filename: string; downloadUrl: string }[] = [];
  for (const msg of messages) {
    const exportToolIds = new Set(
      (msg.toolUses ?? []).filter(tu => tu.name === "export_data").map(tu => tu.id)
    );
    for (const tr of msg.toolResults ?? []) {
      if (!exportToolIds.has(tr.toolUseId)) continue;
      const filename = extractJsonStringField(tr.content, "filename");
      const downloadUrl = extractJsonStringField(tr.content, "download_url");
      if (filename && downloadUrl) {
        cards.push({ filename, downloadUrl });
      }
    }
  }
  return cards;
}

function DownloadCards({ files, onDownload, hasMessages, summaryFile, summaryLoading, onGenerateSummary }: {
  files: { filename: string; downloadUrl: string }[];
  onDownload: (filename: string, downloadUrl: string) => void;
  hasMessages: boolean;
  summaryFile?: { filename: string; downloadUrl: string } | null;
  summaryLoading?: boolean;
  onGenerateSummary?: () => void;
}) {
  // Task 173: return null ONLY when there is nothing at all (0 message). As soon as there are
  // messages, the zone is shown — even with no real file yet — so the download entry point is
  // discoverable (172 hid the whole component on files.length === 0, the discoverability bug).
  if (!hasMessages) return null;
  // If the summary was already generated it is already the first entry of `files` (downloadFiles
  // useMemo) — so only show the "pending" action card when there is no summaryFile yet, to avoid a
  // duplicate. The pending card and any real export cards coexist (pending on top).
  const showPendingSummary = !summaryFile;
  return (
    <div style={{
      padding: "8px 10px",
      borderTop: "1px solid #1f2937",
      flexShrink: 0,
      maxHeight: 220,
      overflowY: "auto",
    }}>
      {showPendingSummary && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "#1f2937",
          border: "1px solid #374151",
          borderRadius: 8,
          padding: "6px 10px",
          marginBottom: 6,
        }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>{"📄"}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: "#e5e7eb", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              Résumé de la conversation
            </div>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>
              {summaryLoading ? "Génération en cours…" : "Document · PDF"}
            </div>
          </div>
          <button
            onClick={onGenerateSummary}
            disabled={summaryLoading || !onGenerateSummary}
            style={{
              background: "transparent",
              color: "#e5e7eb",
              border: "1px solid #374151",
              borderRadius: 999,
              padding: "4px 12px",
              fontSize: 11,
              flexShrink: 0,
              opacity: (summaryLoading || !onGenerateSummary) ? 0.5 : 1,
              cursor: (summaryLoading || !onGenerateSummary) ? "default" : "pointer",
            }}
            title="Générer le résumé de la conversation (PDF)"
          >
            {summaryLoading ? "…" : "Générer"}
          </button>
        </div>
      )}
      {files.map(f => (
        <div key={f.downloadUrl} style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "#1f2937",
          border: "1px solid #374151",
          borderRadius: 8,
          padding: "6px 10px",
          marginBottom: 6,
        }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>{"📄"}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: "#e5e7eb", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {displayTitle(f.filename)}
            </div>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>{fileTypeLabel(f.filename)}</div>
          </div>
          <button
            onClick={() => onDownload(f.filename, f.downloadUrl)}
            style={{
              background: "transparent",
              color: "#e5e7eb",
              border: "1px solid #374151",
              borderRadius: 999,
              padding: "4px 12px",
              fontSize: 11,
              cursor: "pointer",
              flexShrink: 0,
            }}
            title={`Télécharger ${f.filename}`}
          >
            Télécharger
          </button>
        </div>
      ))}
      {files.length > 1 && (
        <button
          onClick={() => { for (const f of files) onDownload(f.filename, f.downloadUrl); }}
          style={{
            background: "transparent",
            color: "#9ca3af",
            border: "1px solid #374151",
            borderRadius: 999,
            padding: "5px 12px",
            fontSize: 12,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span>{"⤓"}</span><span>Tout télécharger</span>
        </button>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────

const headerBtnStyle: React.CSSProperties = {
  background: "transparent",
  color: "#9ca3af",
  border: "none",
  cursor: "pointer",
  fontSize: 14,
  padding: "2px 6px",
};

const sendBtnStyle: React.CSSProperties = {
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  padding: "6px 12px",
  fontSize: 13,
  cursor: "pointer",
  flexShrink: 0,
};
